import { convertToTimezone, convertToUtc } from "@gigpro/utils/datetime";
import type {
  WeekdayEnum,
  WeeklyPattern,
  WorkingInterviewSeriesPreviewRequestRequest,
  WorkingInterviewSeriesRequestRequest,
} from "@sidegigllc/api/client";
import { addDays, addWeeks, differenceInCalendarDays, isAfter, set, startOfDay } from "date-fns";
import * as z from "zod/mini";

/** The API's defaults: a series runs 4 weeks unless the business picks otherwise, and 12 at most. */
const DEFAULT_SERIES_WEEKS = 4;
export const MAX_SERIES_WEEKS = 12;

const DAYS_PER_WEEK = 7;
/** Monday=0, as C3's pattern and the availability picker number them. */
const WEEKDAY_CODES: ReadonlyArray<WeekdayEnum> = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];

const timeOfDay = (date: Date) => date.getHours() * 60 + date.getMinutes();

// Times are `Date`s for `TimeField`; only their time of day is read. Days are calendar days from `DateField`.
export const seriesSchema = z
  .object({
    weekdays: z.array(z.int().check(z.gte(0), z.lte(6))).check(z.minLength(1, "Pick at least one day")),
    startTime: z.date({ error: "Pick a start time" }),
    endTime: z.date({ error: "Pick an end time" }),
    seats: z.int({ error: "Enter how many people" }).check(z.gte(1, "At least one person")),
    startDate: z.date({ error: "Pick a start date" }),
    until: z.date({ error: "Pick an end date" }),
  })
  .check(
    z.superRefine((values, ctx) => {
      // An end at or before the start is overnight, so only an equal time is no shift at all.
      if (timeOfDay(values.startTime) === timeOfDay(values.endTime)) {
        ctx.addIssue({ code: "custom", message: "End time must differ from the start time", path: ["endTime"] });
      }
      const days = differenceInCalendarDays(values.until, values.startDate);
      if (days <= 0) {
        ctx.addIssue({ code: "custom", message: "End date must be after the start date", path: ["until"] });
      } else if (days > MAX_SERIES_WEEKS * DAYS_PER_WEEK) {
        ctx.addIssue({ code: "custom", message: `At most ${MAX_SERIES_WEEKS} weeks`, path: ["until"] });
      }
    }),
  );
export type SeriesValues = z.infer<typeof seriesSchema>;
/** What the step pre-fills: a blank picker has no times yet. */
export type SeriesDefaults = Omit<SeriesValues, "startTime" | "endTime"> & { startTime?: Date; endTime?: Date };

/** `"17:30:00"` as today's 5:30pm, for `TimeField`. */
const parseClock = (clock: string) => {
  const [hours, minutes] = clock.split(":").map(Number);
  return set(new Date(), { hours, minutes, seconds: 0, milliseconds: 0 });
};

/** The suggested weekly pattern (C3) when the location has history, otherwise a blank picker. Starts tomorrow. */
export const seriesDefaults = (
  pattern: WeeklyPattern | null | undefined,
  timezone: string,
  now: Date = new Date(),
): SeriesDefaults => {
  // Tomorrow at the location: near midnight the browser can already be, or still be, on another day.
  const startDate = addDays(startOfDay(convertToTimezone(now, timezone)), 1);
  const dates = { startDate, until: addWeeks(startDate, DEFAULT_SERIES_WEEKS) };
  if (!pattern) return { weekdays: [], seats: 1, ...dates };
  return {
    weekdays: pattern.weekdays,
    startTime: parseClock(pattern.start_time),
    endTime: parseClock(pattern.end_time),
    seats: pattern.headcount,
    ...dates,
  };
};

/** The wall-clock `time` on `day`, read in the location's timezone. */
const atLocation = (day: Date, time: Date, timezone: string) =>
  convertToUtc(set(day, { hours: time.getHours(), minutes: time.getMinutes(), seconds: 0, milliseconds: 0 }), timezone);

/** {@link seriesSchema} plus the rule that needs the location: the first shift starts after `now` there. */
export const seriesSchemaFor = (timezone: string, now: () => Date = () => new Date()) =>
  seriesSchema.check(
    z.superRefine((values, ctx) => {
      if (!isAfter(atLocation(values.startDate, values.startTime, timezone), now())) {
        ctx.addIssue({ code: "custom", message: "Start must be in the future", path: ["startDate"] });
      }
    }),
  );

const toPattern = (values: SeriesValues, timezone: string) => {
  const startTime = atLocation(values.startDate, values.startTime, timezone);
  const byweekdays = values.weekdays
    .toSorted((a, b) => a - b)
    .map((day) => ({ weekday: WEEKDAY_CODES[day], occurrence: 1 }));
  return {
    startTime,
    recurringPattern: {
      freq: "WEEKLY",
      interval: 1,
      byweekdays,
      // The location's own days, so a 9pm Friday stays Friday.
      weekdays_are_local: true,
      // At the shift's start time, as the API's own default reads: the last day counts and the cap is 12 weeks.
      until: atLocation(values.until, values.startTime, timezone),
    } satisfies WorkingInterviewSeriesRequestRequest["recurring_pattern"],
  };
};

export const toPreviewRequest = (
  values: SeriesValues,
  timezone: string,
): WorkingInterviewSeriesPreviewRequestRequest => {
  const { startTime, recurringPattern } = toPattern(values, timezone);
  return { start_time: startTime, recurring_pattern: recurringPattern };
};

export const toSeriesRequest = (values: SeriesValues, timezone: string): WorkingInterviewSeriesRequestRequest => {
  const { startTime, recurringPattern } = toPattern(values, timezone);
  let endTime = atLocation(values.startDate, values.endTime, timezone);
  if (!isAfter(endTime, startTime)) endTime = atLocation(addDays(values.startDate, 1), values.endTime, timezone);
  return {
    start_time: startTime,
    end_time: endTime,
    recurring_pattern: recurringPattern,
    allowed_confirmed_pros: values.seats,
  };
};

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

const listDays = (weekdays: ReadonlyArray<number>) => {
  const names = weekdays.toSorted((a, b) => a - b).map((day) => DAY_NAMES[day]);
  if (names.length === DAYS_PER_WEEK) return "day";
  return names.length > 1 ? `${names.slice(0, -1).join(", ")} and ${names.at(-1)}` : (names[0] ?? "");
};

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

/** What the business gets, read off the server's preview: "Every Fri and Sat · 2 people per shift · 8 working
 *  interviews through Nov 13". Decision 4: nothing is posted that it didn't see. */
export const seriesSummary = (
  { weekdays, seats }: Pick<SeriesValues, "weekdays" | "seats">,
  preview: { count: number; until: Date },
  formatDay: (date: Date) => string,
) =>
  [
    `Every ${listDays(weekdays)}`,
    `${plural(seats, "person", "people")} per shift`,
    `${plural(preview.count, "working interview", "working interviews")} through ${formatDay(preview.until)}`,
  ].join(" · ");
